/**
 * descriptionExtractor.js
 * -------------------------
 * Smart parser that pulls Fabric and Embroidery / Technique values out of a
 * free-text product description written in the Silk & Sentiment style.
 *
 * Powers the Admin "Auto-fill from Description" button and the bulk-sync
 * action. The parser is deliberately conservative — it returns clean Title
 * Case strings and never invents detail that isn't in the description.
 *
 * Recognised vocab can be extended freely.
 */

// --- Fabrics -------------------------------------------------------------
// Order matters: longer, more specific phrases first so "raw silk" wins
// over "silk".
const FABRIC_PATTERNS = [
  { test: /\b(pure\s+chiffon)\b/i,         label: 'Pure Chiffon' },
  { test: /\b(pure\s+georgette)\b/i,       label: 'Pure Georgette' },
  { test: /\b(pure\s+organza)\b/i,         label: 'Pure Organza' },
  { test: /\b(pure\s+silk)\b/i,            label: 'Pure Silk' },
  { test: /\b(raw\s+silk)\b/i,             label: 'Raw Silk' },
  { test: /\b(mulberry\s+silk)\b/i,        label: 'Mulberry Silk' },
  { test: /\b(tussar\s+silk|tussar)\b/i,   label: 'Tussar Silk' },
  { test: /\b(banarasi\s+silk|banarasi)\b/i, label: 'Banarasi Silk' },
  { test: /\b(kanjivaram|kanchipuram)\b/i, label: 'Kanjivaram Silk' },
  { test: /\b(tissue\s+silk)\b/i,          label: 'Tissue Silk' },
  { test: /\b(tissue)\b/i,                 label: 'Tissue' },
  { test: /\b(silk)\b/i,                   label: 'Silk' },
  { test: /\b(velvet)\b/i,                 label: 'Velvet' },
  { test: /\b(chiffon)\b/i,                label: 'Chiffon' },
  { test: /\b(georgette)\b/i,              label: 'Georgette' },
  { test: /\b(organza)\b/i,                label: 'Organza' },
  { test: /\b(crepe)\b/i,                  label: 'Crepe' },
  { test: /\b(satin)\b/i,                  label: 'Satin' },
  { test: /\b(brocade)\b/i,                label: 'Brocade' },
  { test: /\b(net|tulle)\b/i,              label: 'Net' },
  { test: /\b(khadi)\b/i,                  label: 'Khadi' },
  { test: /\b(linen)\b/i,                  label: 'Linen' },
  { test: /\b(cotton)\b/i,                 label: 'Cotton' },
  { test: /\b(patola)\b/i,                 label: 'Patola' },
  { test: /\b(bandhani|bandhej)\b/i,       label: 'Bandhani' },
];

// --- Embroidery / techniques --------------------------------------------
const EMBROIDERY_PATTERNS = [
  { test: /\bzardozi\b/i,            label: 'Zardozi' },
  { test: /\bchikankari\b/i,         label: 'Chikankari' },
  { test: /\bdabka\b/i,              label: 'Dabka' },
  { test: /\bnaqshi\b/i,             label: 'Naqshi' },
  { test: /\b(moti(?:\s+work)?|pearl(?:\s+work)?)\b/i, label: 'Moti Work' },
  { test: /\bresham\b/i,             label: 'Resham' },
  { test: /\baari(?:\s+work)?\b/i,   label: 'Aari' },
  { test: /\bkamdani\b/i,            label: 'Kamdani' },
  { test: /\bmukaish\b/i,            label: 'Mukaish' },
  { test: /\bgota(?:\s+patti)?\b/i,  label: 'Gota Patti' },
  { test: /\b(zari(?:\s+work)?)\b/i, label: 'Zari' },
  { test: /\bphulkari\b/i,           label: 'Phulkari' },
  { test: /\bkantha\b/i,             label: 'Kantha' },
  { test: /\b(kashida|kashmiri)\b/i, label: 'Kashmiri' },
  { test: /\bkundan\b/i,             label: 'Kundan' },
  { test: /\b(stone\s+work|stones)\b/i, label: 'Stone Work' },
  { test: /\b(mirror\s+work|shisha)\b/i, label: 'Mirror Work' },
  { test: /\b(sequin(?:s)?(?:\s+work)?)\b/i, label: 'Sequins' },
  { test: /\b(thread\s+work)\b/i,    label: 'Thread Work' },
];

const formatList = (items) => {
  if (items.length === 0) return '';
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} & ${items[1]}`;
  return `${items.slice(0, -1).join(', ')} & ${items[items.length - 1]}`;
};

const extractMatches = (text, patterns) => {
  if (!text) return [];
  const masked = String(text);
  const found = [];
  let working = masked;
  // Walk the patterns in priority order; once a pattern matches, blank out
  // its hit so a more generic pattern (e.g. "silk") can't double-count.
  for (const { test, label } of patterns) {
    if (test.test(working)) {
      if (!found.includes(label)) found.push(label);
      working = working.replace(test, ' '.repeat(label.length));
    }
  }
  return found;
};

/**
 * extractFromDescription(text) → { fabric, embroidery }
 * Returns Title-Case formatted strings ready to drop into admin form fields.
 */
export const extractFromDescription = (text) => {
  if (!text || !String(text).trim()) return { fabric: '', embroidery: '' };
  const fabrics = extractMatches(text, FABRIC_PATTERNS);
  const embroideries = extractMatches(text, EMBROIDERY_PATTERNS);
  return {
    fabric: formatList(fabrics),
    embroidery: formatList(embroideries),
  };
};

export default extractFromDescription;
